import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart, ArrowRight, ShieldCheck, Leaf, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Seo from "@/components/Seo";
import FaqSection, { faqJsonLd } from "@/components/FaqSection";
import { api } from "@/lib/api";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1758691031958-29541f3207a5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

const HERO_IMG =
  "https://images.pexels.com/photos/7086032/pexels-photo-7086032.jpeg?auto=compress&cs=tinysrgb&w=1600";

const FAQ_ITEMS = [
  {
    q: "When should my parent move to assisted living?",
    a: "There's rarely one moment. The clearest signs are stacked together: missed medications, weight loss, mail piling up, fear of falling, social isolation, or caregivers feeling burned out. If two or more of these are happening, it's time to start exploring options — even if a move isn't immediate. Our quiz helps you spot which level of care fits today's reality, not last year's.",
  },
  {
    q: "What's the difference between assisted living and memory care?",
    a: "Assisted living supports daily tasks (bathing, dressing, medications) while preserving independence and a private apartment. Memory care is a secured, dementia-specific environment with specially trained staff, structured routines, and design choices that reduce confusion. If your loved one wanders, gets disoriented in familiar places, or has been diagnosed with Alzheimer's or dementia, memory care is usually the safer fit.",
  },
  {
    q: "How much does senior care cost?",
    a: "In the US in 2026, assisted living averages $5,500–$6,500/month, memory care $7,000–$9,000/month, and independent living $3,500–$5,000/month. Costs vary significantly by region, level of care, and amenities. Long-term care insurance, VA benefits (Aid & Attendance), and home equity are the most common funding sources. We'll help you find communities within your budget — never above it.",
  },
  {
    q: "Does Medicare or insurance cover assisted living?",
    a: "Medicare does NOT cover the room-and-board cost of assisted living or memory care. It covers short-term skilled nursing after a hospital stay only. Long-term care insurance (if purchased before care is needed) covers most of it. Medicaid covers some assisted living in many states under specific waivers, but the rules are complex. VA Aid & Attendance can add $1,500–$2,800/month for qualifying veterans and surviving spouses.",
  },
  {
    q: "What's the difference between independent living and assisted living?",
    a: "Independent living is for active seniors who want a maintenance-free lifestyle, social community, dining, and amenities — no help with daily tasks. Assisted living adds support with bathing, dressing, medications, and mobility. Many communities offer both side-by-side, so residents can transition between levels as needs change without moving buildings.",
  },
  {
    q: "How do I know if it's time for memory care?",
    a: "Common triggers: wandering or getting lost, leaving the stove on, mistaking strangers for family, becoming agitated in the late afternoon (sundowning), or aggression toward caregivers. If safety has become a daily worry, memory care's secured environment and trained staff usually relieve that worry within the first week.",
  },
  {
    q: "What happens after I take the CareHomeConnect quiz?",
    a: "Right away, you'll see your recommended level of care and why it fits. Within 24 hours, a vetted local senior-living advisor will reach out with 2–3 communities matched to your zip code, budget, and care needs. The service is 100% free for families — communities pay us a small referral fee only after a successful move-in. You're never obligated to choose any of them.",
  },
  {
    q: "How long does it take to move into a senior living community?",
    a: "If a unit is available, move-in can happen in as little as 1–2 weeks. The typical timeline from first tour to move-in is 30–60 days. Memory care often has waitlists in popular metros (4–8 weeks). If your situation is urgent — recent fall, hospital discharge — let your advisor know; many communities can fast-track for medical urgency.",
  },
  {
    q: "Will I be hassled by salespeople if I take the quiz?",
    a: "No. We send your match to one specialist (not 5+ like big lead-gen sites), and they reach out by your preferred channel. We never sell your data, and you can opt out of all contact with one reply. Our partners are vetted for compassionate, non-pushy outreach — that's what makes families willing to take the quiz in the first place.",
  },
];

export default function Landing() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles/public").then(({ data }) => setArticles(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title="Find the right senior care for your family"
        description="Honest, easy-to-read guidance for families navigating senior care. Take our free 3-minute quiz for a personalized recommendation — assisted living, memory care, independent living, and more. No pressure, no cost."
        path="/"
        keywords="senior care, assisted living, memory care, independent living, senior living advisor, senior care finder, find senior care, memory care quiz, assisted living quiz, senior care for parents"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "CareHomeConnect — Find the right senior care",
            "description": "Free, no-pressure senior care guidance and quiz to help families choose between assisted living, memory care, and independent living.",
            "url": "https://carehomeconnect.org/",
            "publisher": {
              "@type": "Organization",
              "name": "CareHomeConnect",
              "url": "https://carehomeconnect.org",
            },
          },
          faqJsonLd(FAQ_ITEMS),
        ]}
      />
      <PublicNav />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-6">
              <Leaf className="w-3.5 h-3.5" />
              Trusted by thousands of families
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A2E24] tracking-tight leading-[1.05]">
              Finding the right care for the people you love
              <span className="text-[#9CAF88]">.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C6B62] leading-relaxed max-w-xl">
              No pressure. No sales pitch. Just clarity. Answer a few gentle questions and we'll help you understand the
              best next step for your family — in under 3 minutes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/quiz" data-testid="hero-start-quiz">
                <Button className="bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-14 px-8 text-base font-medium group">
                  Find the best care option
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition" />
                </Button>
              </Link>
              <Link to="/articles" data-testid="hero-learn-more">
                <Button variant="ghost" className="rounded-xl h-14 px-8 text-base font-medium text-[#2C4C3B] hover:bg-[#9CAF88]/10">
                  Read stories first
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-[#5C6B62]">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[#9CAF88]" /> Private & secure
              </div>
              <div className="flex items-center gap-1.5">
                <Heart className="w-4 h-4 text-[#E07A5F]" /> No cost to families
              </div>
            </div>
          </div>
          <div className="relative animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            <div className="absolute -top-6 -left-6 w-40 h-40 bg-[#9CAF88]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -right-6 w-56 h-56 bg-[#E07A5F]/15 rounded-full blur-3xl" />
            <img
              src={HERO_IMG}
              alt="Grandparents sharing a joyful hug with their granddaughter — the families CareHomeConnect helps"
              className="relative rounded-3xl shadow-[0_24px_60px_rgba(44,76,59,0.18)] object-cover w-full aspect-[4/5]"
            />
            <div className="absolute -bottom-6 left-6 bg-white rounded-2xl p-4 shadow-[0_12px_40px_rgba(44,76,59,0.15)] flex items-center gap-3 max-w-[230px]">
              <div className="w-10 h-10 rounded-full bg-[#9CAF88]/20 flex items-center justify-center">
                <Phone className="w-4 h-4 text-[#2C4C3B]" />
              </div>
              <div className="text-xs text-[#5C6B62] leading-tight">
                <div className="font-semibold text-[#2C4C3B] text-sm">Local specialists</div>
                Ready to help in your area
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three benefits */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Personalized guidance", body: "A short quiz matches you with the right level of care — Independent, Assisted, or Memory Care.", icon: Heart },
            { title: "Zero pressure", body: "We share options. You decide when — or if — you're ready to talk to anyone.", icon: ShieldCheck },
            { title: "Always free to families", body: "Our service is free. Partners only pay us when they successfully help a family.", icon: Leaf },
          ].map((item, i) => (
            <div key={i} className="card-soft p-8">
              <div className="w-11 h-11 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center mb-4">
                <item.icon className="w-5 h-5 text-[#2C4C3B]" />
              </div>
              <h3 className="font-display text-xl font-semibold text-[#1A2E24] mb-2">{item.title}</h3>
              <p className="text-sm text-[#5C6B62] leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured articles */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-sm text-[#9CAF88] font-semibold mb-2 uppercase tracking-wider">Real stories, real guidance</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24] max-w-xl">
              Questions every family is asking right now
            </h2>
          </div>
          <Link to="/articles" className="hidden md:inline-flex items-center gap-2 text-[#2C4C3B] font-medium hover:underline">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <Link
              to={`/articles/${a.slug}`}
              key={a.id}
              className="card-soft p-0 overflow-hidden group"
              data-testid={`landing-article-${a.slug}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#E2DFD8]">
                <img
                  src={a.cover_image}
                  alt={a.title}
                  onError={(e) => { if (e.currentTarget.src !== FALLBACK_IMAGE) e.currentTarget.src = FALLBACK_IMAGE; }}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                />
              </div>
              <div className="p-6">
                <div className="text-xs font-medium text-[#9CAF88] uppercase tracking-wider mb-2">
                  {a.category}
                </div>
                <h3 className="font-display text-lg font-semibold text-[#1A2E24] leading-snug mb-2 group-hover:text-[#2C4C3B]">
                  {a.title}
                </h3>
                <p className="text-sm text-[#5C6B62] line-clamp-2">{a.hook}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <FaqSection
        eyebrow="Common questions"
        title="Senior care, plainly explained"
        description="The things every family is wondering about. No fluff, no marketing-speak — just clear answers from people who do this every day."
        items={FAQ_ITEMS}
        testIdPrefix="landing-faq"
      />

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="bg-[#2C4C3B] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#9CAF88]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          <div className="relative">
            <h2 className="font-display text-3xl md:text-5xl font-semibold text-white max-w-2xl mx-auto leading-tight">
              You don't have to figure this out alone.
            </h2>
            <p className="mt-6 text-[#F7F5F0]/80 max-w-lg mx-auto">
              Answer a few simple questions and get a personalized recommendation in about 3 minutes.
            </p>
            <Link to="/quiz" data-testid="cta-final-quiz">
              <Button className="mt-8 bg-[#E07A5F] hover:bg-[#C96B52] text-white rounded-xl h-14 px-10 text-base font-medium">
                Start the quiz — it's free
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
